// Every way the loaded rows can be narrowed, in one place. There are now five predicates — text,
// missing-score, sector, index membership and the knowable-at window — and they feed the row list,
// the match count, the score range and the paging reset alike, so keeping them together is what
// stops those five from disagreeing about what is on screen.
//
// Index membership is the one whose filter value is a COLLECTION, because the field it reads is one:
// a row holds a set of indices, so the question "which of them" has to be able to name several. It
// is still ONE predicate ANDed with the other four — the several are OR'd inside it. Anything that
// reads filters.memberships must check its LENGTH; `[]` is truthy and the falsy-means-off shortcut
// the other four use silently inverts here.
//
// The knowable-at window is the only one whose bounds are not values off the rows themselves, so the
// day arithmetic behind it — and the UTC-vs-local question it turns on — lives in knowableWindow.js.
// What stays here is the predicate.
//
// `missingOnly` is named for the score, not the inputs, and the distinction is load-bearing: the
// predicate is `score === null`, which on a single-input feed means the score itself is absent and
// NOT that an input went missing — that feed has no inputs to lose. The table's copy and the
// toggle's label both stopped saying "inputs" unconditionally for exactly that reason, so this
// comment must not put the old name back on the thing they were corrected away from.

import { isNarrowable, utcDayEnd, utcDayStart } from './knowableWindow';

// The "belongs to no index" selection. Not a real index, and deliberately NOT something
// membershipOptions can ever return: that function answers "which indices are in this result", and
// the answer must not gain a member that is not one. Spelled so no wire key can collide with it —
// the server's keys are [a-z0-9_] identifiers with no leading underscore.
export const NO_INDEX = '__no_index__';

export const EMPTY_FILTERS = {
  query: '',
  missingOnly: false,
  sector: '',
  // A SET of index keys rather than one, because "S&P 500 or Dow 30" is one question about one
  // set-valued field and not two questions to be ANDed — see matchesMemberships for why ANDing them
  // would be a control that can only select nothing.
  //
  // Frozen because this object is the initial state of every FeedRowsTable and the value its reset
  // effect assigns: an in-place push here would hand the next mount a selection it never made.
  // Modules are strict, so the mistake throws rather than corrupting the constant quietly.
  memberships: Object.freeze([]),
  // Calendar dates at local midnight, per the contract in knowableWindow.js. Null means unbounded on
  // that side, which is why they are null and not '' — an empty string is a Date the pickers would
  // have to invent.
  knowableFrom: null,
  knowableTo: null,
};

const rowLabels = (row) =>
  [row.symbol, row.companyName, row.companyId]
    .filter((field) => field !== null && field !== undefined)
    .map((field) => String(field).toLowerCase());

// `ticker` is resolved point-in-time as-of each row's knowable_at, so one company can appear in a
// single result under two symbols — a rename inside the window gives its older rows the older
// ticker. Matching a row against only its own labels therefore made a search for the company's
// current ticker silently drop exactly the history the viewer exists to show. The needle is instead
// matched against every label the company carried ANYWHERE in this result, keyed on company_id,
// which is the row's only stable identity.
//
// Built over the whole loaded set rather than the surviving rows: the index has to be complete
// before any filtering happens, or the older rows it is meant to rescue are already gone.
const buildCompanyLabelIndex = (rows) => {
  const index = new Map();
  (rows || []).forEach((row) => {
    if (row.companyId === null || row.companyId === undefined) return;
    const key = String(row.companyId);
    const labels = index.get(key) || new Set();
    rowLabels(row).forEach((label) => labels.add(label));
    index.set(key, labels);
  });
  return index;
};

// A row with no company_id has no identity to group by, so it can only answer for itself.
const labelsFor = (row, index) => {
  const grouped = row.companyId === null || row.companyId === undefined ? null : index.get(String(row.companyId));
  return grouped ? Array.from(grouped) : rowLabels(row);
};

const matchesQuery = (row, needle, index) => !needle || labelsFor(row, index).some((label) => label.includes(needle));

const matchesSector = (row, sector) => !sector || row.sector === sector;

// OR'd across the selection: a row survives if it is in ANY selected index. AND would be degenerate
// for almost every pair this vocabulary can form — the market-cap bands are rank prefixes of each
// other (top 1..1000 inside top 1..5000) and djia30 sits inside sp500 — so an intersection is either
// empty or exactly the narrower selection, which is the select-that-narrows-to-nothing the options
// comment below exists to prevent.
//
// `selected` arrives as a Set, or as null for "no filter", both resolved once in applyRowFilters.
// The null is not a convenience: an empty ARRAY is truthy, so the `!membership` escape hatch this
// predicate used to open with never fires on a collection, and every row would drop.
//
// NO_INDEX is one more disjunct of that same union, never a second facet — `sp500 AND no-index` is
// unsatisfiable. It answers only for a row whose flags were actually REQUESTED: a row of a
// floor-only read holds no memberships either, and calling that "in no index" would report the
// absence of a question as an answer to it.
const matchesMemberships = (row, selected) => {
  if (selected === null) return true;
  const keys = row.memberships || [];
  if (keys.length === 0) return row.hasMemberships === true && selected.has(NO_INDEX);
  return keys.some((key) => selected.has(key));
};

const matchesMissingOnly = (row, missingOnly) => !missingOnly || row.score === null;

// Inclusive on both ends, over the UTC day each bound names — see knowableWindow.js for why the
// conversion is not optional.
//
// A row with no knowable_at is DROPPED as soon as either bound is set. The column renders such a row
// as an em dash, and neither answer is comfortable: keeping it asserts the row falls inside a window
// it has no date to be inside of, dropping it asserts the opposite. Dropping is the one that matches
// what was asked — a date filter is a question about dates, and a row with no date cannot answer it —
// and it is the direction the row count already explains, since the `N of M rows match` line moves.
// With both bounds unset every row survives, em dash included.
const matchesKnowableWindow = (row, fromMs, toMs) => {
  if (fromMs === null && toMs === null) return true;
  const time = row.knowableAt ? new Date(row.knowableAt).getTime() : NaN;
  if (isNaN(time)) return false;
  if (fromMs !== null && time < fromMs) return false;
  if (toMs !== null && time > toMs) return false;
  return true;
};

export const applyRowFilters = (rows, filters = EMPTY_FILTERS) => {
  const list = rows || [];
  const needle = String(filters.query || '')
    .trim()
    .toLowerCase();
  // Only the text filter needs the index, and only the index costs a pass over every row.
  const index = needle ? buildCompanyLabelIndex(list) : null;
  // Resolved once rather than per row: the conversion reads a Date's fields and rebuilds it, and the
  // bounds are the same for every row in the pass.
  const fromMs = utcDayStart(filters.knowableFrom);
  const toMs = utcDayEnd(filters.knowableTo);
  // Null rather than an empty Set, so the predicate's "no filter" case is a reference check and not
  // a size check per row.
  const selected = (filters.memberships || []).length ? new Set(filters.memberships) : null;

  return list.filter(
    (row) =>
      matchesMissingOnly(row, filters.missingOnly) &&
      matchesQuery(row, needle, index) &&
      matchesSector(row, filters.sector) &&
      matchesMemberships(row, selected) &&
      matchesKnowableWindow(row, fromMs, toMs)
  );
};

// Drop any filter whose vocabulary the current response no longer has.
//
// Every control in the bar is gated on the CURRENT response — a sector select only exists while the
// rows carry sectors — but the state those controls write outlived them: filters reset on a feed
// CHANGE, not on a new response of the same feed. So a sector chosen against one read could still be
// narrowing the next one with no select on screen to say so, and the only clear-all lived inside
// IndexFilter, itself behind an early return.
//
// The table's own note admitted this and called it "visible and one press from undone" — but the
// block it points at states the count and offers no control, and when the select has unmounted there
// is no press to make.
//
// Pure, and keyed on AVAILABILITY rather than on the rows, so it answers the same question the bar
// answers: not "does anything match" but "can the analyst still see and change this". A filter that
// matches nothing is a real answer; a filter that cannot be reached is a trap.
//
// `query` and `missingOnly` are never pruned. The text box is unconditional, and the missing-only
// toggle hides itself when a response has no gaps — but a response with no gaps also has nothing for
// it to hide, so leaving it set changes nothing and clearing it would silently undo a choice the
// analyst can still see the moment a gap comes back.
export const pruneFilters = (filters = EMPTY_FILTERS, availability = {}) => {
  const { sectors = [], indexOptions = [], hasUnindexed = false, knowableSpan = null } = availability;

  const keptMemberships = (filters.memberships || []).filter(
    (key) => indexOptions.includes(key) || (key === NO_INDEX && hasUnindexed)
  );
  const keepSector = !filters.sector || sectors.includes(filters.sector);
  const keepWindow = isNarrowable(knowableSpan);

  const next = {
    ...filters,
    sector: keepSector ? filters.sector : '',
    memberships: keptMemberships,
    knowableFrom: keepWindow ? filters.knowableFrom : null,
    knowableTo: keepWindow ? filters.knowableTo : null,
  };

  // Referentially stable when nothing was pruned: this feeds a memo and a prop, and a fresh object
  // every render would defeat both.
  const unchanged =
    next.sector === filters.sector &&
    next.knowableFrom === filters.knowableFrom &&
    next.knowableTo === filters.knowableTo &&
    keptMemberships.length === (filters.memberships || []).length;

  return unchanged ? filters : next;
};

export const isFiltering = (filters = EMPTY_FILTERS) =>
  !!(
    filters.query ||
    filters.missingOnly ||
    filters.sector ||
    // By LENGTH, never by truthiness: `[]` is truthy, so the bare `filters.membership ||` this line
    // used to be would report every unfiltered table as filtered and put the "no loaded row matches
    // these filters" block under an empty result that nobody had filtered.
    (filters.memberships || []).length > 0 ||
    filters.knowableFrom ||
    filters.knowableTo
  );

// Add or remove one index, always returning a NEW array. updateFilters merges by reference and the
// paging reset keys on the filters object's identity (FeedRowsTable.js), so a mutation would both
// poison EMPTY_FILTERS and skip the reset it was supposed to trigger.
export const toggleMembership = (selected, key) => {
  const list = selected || [];
  return list.includes(key) ? list.filter((entry) => entry !== key) : [...list, key];
};

// The options each select offers: what the response actually contains, so a filter can never select
// nothing. Sorted for a stable list rather than response order.
export const sectorOptions = (rows) =>
  Array.from(new Set((rows || []).map((row) => row.sector).filter(Boolean))).sort();

// `.filter(Boolean)` for the same reason sectorOptions has one: a wire field spelled exactly
// `is_member_of_` slices to '', which was harmless while '' was the select's "all indices" sentinel
// and is not now that every option is a chip you can press.
export const membershipOptions = (rows) =>
  Array.from(new Set((rows || []).flatMap((row) => row.memberships || []).filter(Boolean))).sort();

// Whether offering the NO_INDEX chip would mean anything: some row was asked about its indices and
// turned out to be in none. `hasMemberships` is the only thing separating that from a read where
// nobody asked, and the two are identical on the row itself.
export const hasUnindexedRows = (rows) =>
  (rows || []).some((row) => row.hasMemberships && (row.memberships || []).length === 0);
